/**
 * Slide-over drawer listing users that have at least one org assignment.
 * Opens when the user clicks the "Active users" KPI tile or the
 * Users button in the directory. Admin-only (mirrors the /api/directory/users endpoint).
 */

import {
  Button,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  Input,
  OverlayDrawer,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { DismissRegular, SearchRegular } from '@fluentui/react-icons';
import { useEffect, useMemo, useState } from 'react';

import type { UserRow } from '@/api/types';
import { DataTable, EmptyState, ErrorState, LoadingState, type DataTableColumn } from '@/design-system';
import { spacing } from '@/theme/tokens';

import { useUsers } from '../api';
import { StatusBadge } from './StatusBadge';

interface UsersPanelProps {
  open: boolean;
  onClose: () => void;
}

const useStyles = makeStyles({
  toolbar: {
    marginBottom: spacing.m,
  },
  tableWrap: {
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    overflow: 'hidden',
  },
  meta: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    marginTop: spacing.xs,
    display: 'block',
  },
});

function UsersTable({ rows, partial }: { rows: readonly UserRow[]; partial: boolean }) {
  const columns = useMemo<readonly DataTableColumn<UserRow>[]>(() => [
    { id: 'name', header: 'Name', getValue: (item) => item.displayName, renderCell: (item) => item.displayName, sortable: !partial },
    { id: 'email', header: 'Email', getValue: (item) => item.emailAddress, renderCell: (item) => item.emailAddress, width: 280, sortable: !partial },
    { id: 'status', header: 'Status', getValue: (item) => item.status, renderCell: (item) => <StatusBadge status={item.status} />, sortable: !partial },
  ], [partial]);
  return <DataTable ariaLabel="Users" columns={columns} getRowId={(item) => item.userId} items={rows} tableId="directory-users" sortDescription={partial ? 'User order follows the server order because this is a partial result.' : undefined} emptyState={<EmptyState title="No users match the filter" description="Try a shorter or different search query." />} />;
}

function UsersPanelContent() {
  const classes = useStyles();
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  // Debounce: wait 300 ms after last keystroke before issuing a new server request.
  useEffect(() => {
    const id = setTimeout(() => { setDebouncedQuery(query); }, 300);
    return () => { clearTimeout(id); };
  }, [query]);

  const { data, isLoading, isFetching, error, refetch } = useUsers(debouncedQuery);

  const items = data?.items ?? [];
  return (
    <>
      <div className={classes.toolbar}>
        <Input
          contentBefore={<SearchRegular />}
          placeholder="Search by name or email"
          value={query}
          onChange={(_, d) => {
            setQuery(d.value);
          }}
          aria-label="Search users"
        />
        {!isLoading && !error && (
          <Text className={classes.meta}>
            {items.length.toLocaleString()} users
            {data?.isCapped
              ? ' — list capped at 1,000. Use search to narrow results.'
              : ''}
          </Text>
        )}
      </div>
      {error ? (
        <ErrorState
          title="Couldn't load users"
          description={error.message}
          retry={() => void refetch()}
        />
      ) : isLoading || isFetching ? (
        <LoadingState label={isFetching && !isLoading ? 'Searching\u2026' : 'Loading users\u2026'} />
      ) : (
        <UsersTable rows={items} partial={data?.isCapped ?? false} />
      )}
    </>
  );
}

export function UsersPanel({ open, onClose }: UsersPanelProps) {
  return (
    <OverlayDrawer
      open={open}
      position="end"
      size="medium"
      onOpenChange={(_, state) => {
        if (!state.open) onClose();
      }}
    >
      <DrawerHeader>
        <DrawerHeaderTitle
          action={
            <Button
              appearance="subtle"
              aria-label="Close panel"
              icon={<DismissRegular />}
              onClick={onClose}
            />
          }
        >
          Users
        </DrawerHeaderTitle>
      </DrawerHeader>
      <DrawerBody>
        {open ? <UsersPanelContent /> : null}
      </DrawerBody>
    </OverlayDrawer>
  );
}
